import { Alert, Button, Field } from '@catering/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useState } from 'react';

import { hasPermission } from '../../../auth/has-permission';
import { useCurrentUser } from '../../../auth/use-current-user';
import type { MenuDetail } from '../../../menus/api';
import { updateMenu } from '../../../menus/api';
import { mapMenuPatchError } from '../menu-form-schema';

export interface MenuPreparationNotesTabProps {
  menu: MenuDetail;
}

/**
 * `menus/spec.md` → "Notas de preparación visibles solo para cocina". Only
 * rendered by `MenuDetailPage` when `preparationNotes` is present in the
 * response — the absence of the key is the only signal this component
 * needs (`design.md` §7).
 */
export function MenuPreparationNotesTab({ menu }: MenuPreparationNotesTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draft, setDraft] = useState(menu.preparationNotes ?? '');
  const currentUser = useCurrentUser();
  const readOnly = menu.deletedAt !== null || !hasPermission(currentUser.data, 'menu.update');

  const mutation = useMutation({
    mutationFn: (preparationNotes: string) =>
      updateMenu(menu.id, { preparationNotes: preparationNotes === '' ? null : preparationNotes }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['menu', menu.id] });
      setMode('view');
    },
  });

  if (mode === 'edit') {
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate(draft);
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        {mutation.isError ? <Alert tone="danger">{mapMenuPatchError(mutation.error)}</Alert> : null}

        <Field
          label="Notas de preparación"
          name="preparationNotes"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />

        <div className="flex items-center gap-3">
          <Button type="submit" loading={mutation.isPending}>
            Guardar
          </Button>
          <Button type="button" variant="secondary" onClick={() => setMode('view')}>
            Cancelar
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-slate-900">
        {menu.preparationNotes ?? 'Sin notas de preparación cargadas.'}
      </p>
      {!readOnly ? (
        <Button
          onClick={() => {
            setDraft(menu.preparationNotes ?? '');
            setMode('edit');
          }}
        >
          Editar
        </Button>
      ) : null}
    </div>
  );
}
